/**
 * Organization API Service
 * CRUD operations for organizations in the RILE Connect ecosystem
 */

import { useMemo } from 'react';

import { useAuthenticatedAxios } from './axiosInstance';

// hook for organization API operations
export const useOrganizationApi = () => {
    const api = useAuthenticatedAxios();

    return useMemo(
        () => ({
            /**
             * Fetch all approved organizations (public)
             * @param {Object} params - Query parameters (page, limit, sector, region)
             * @returns {Promise<Object>} Paginated organizations list
             */
            fetchOrganizations: async (params = {}) => {
                const response = await api.get('/api/organizations', {
                    params,
                });
                return response.data;
            },

            /**
             * Search organizations (public)
             * @param {string} query - Search query
             * @param {Object} filters - Filter options (sector, region, programmingTypes)
             * @returns {Promise<Object>} Search results
             */
            searchOrganizations: async (query, filters = {}) => {
                const response = await api.get('/api/organizations/search', {
                    params: { q: query, ...filters },
                });
                return response.data;
            },

            /**
             * Fetch a random selection of approved organizations for carousels (public)
             * @param {number} limit - Number of organizations to return (default: 9)
             * @returns {Promise<Object>} Random organizations
             */
            fetchFeaturedOrganizations: async (limit = 9) => {
                const response = await api.get('/api/organizations/featured', {
                    params: { limit },
                });
                return response.data;
            },

            /**
             * Fetch single organization by ID (public)
             * @param {string} id - Organization ID
             * @returns {Promise<Object>} Organization details
             */
            fetchOrganization: async (id) => {
                const response = await api.get(`/api/organizations/${id}`);
                return response.data;
            },

            /**
             * Check if an organization name is already taken (public)
             * @param {string} name - Organization name to check
             * @returns {Promise<Object>} { exists: boolean, name?: string }
             */
            checkName: async (name) => {
                const response = await api.get(
                    '/api/organizations/check-name',
                    { params: { name } }
                );
                return response.data;
            },

            /**
             * Create a new organization (authenticated)
             * @param {Object} organizationData - Organization details
             * @returns {Promise<Object>} Created organization
             */
            createOrganization: async (organizationData) => {
                const response = await api.post(
                    '/api/organizations',
                    organizationData
                );
                return response.data;
            },

            /**
             * Update an organization (authenticated, owner or admin)
             * @param {string} id - Organization ID
             * @param {Object} organizationData - Updated organization details
             * @returns {Promise<Object>} Updated organization
             */
            updateOrganization: async (id, organizationData) => {
                const response = await api.put(
                    `/api/organizations/${id}`,
                    organizationData
                );
                return response.data;
            },

            /**
             * Delete an organization (authenticated, owner or admin)
             * @param {string} id - Organization ID
             * @returns {Promise<Object>} Deletion confirmation
             */
            deleteOrganization: async (id) => {
                const response = await api.delete(`/api/organizations/${id}`);
                return response.data;
            },

            /**
             * Fetch pending organizations (admin only)
             * @returns {Promise<Array>} List of pending organizations
             */
            fetchPendingOrganizations: async () => {
                const response = await api.get(
                    '/api/organizations/admin/pending'
                );
                return response.data;
            },

            /**
             * Update organization status (admin only)
             * @param {string} id - Organization ID
             * @param {string} status - New status ('approved' or 'rejected')
             * @param {string} adminNotes - Optional admin notes
             * @returns {Promise<Object>} Updated organization
             */
            updateOrganizationStatus: async (id, status, adminNotes = '') => {
                const response = await api.put(
                    `/api/organizations/${id}/status`,
                    { status, adminNotes }
                );
                return response.data;
            },

            // The legacy `getSectors`, `getRegions`, and
            // `getProgrammingTypes` methods previously returned hardcoded
            // Colorado-shaped fallback arrays inline (so they "worked" without
            // the server). Those fallbacks have been removed because they
            // leaked COSTEM-shaped defaults into every tenant. The methods
            // are now thin passthroughs to placeholder tenant-aware enum
            // endpoints. Wave M (server) lands those endpoints; Wave R then
            // rewires the consumers in OrganizationOnboarding /
            // OrganizationEdit / OpportunityForm to read
            // `ecosystem.taxonomy.{sectors,regions,programmingTypes}` from
            // EcosystemContext directly and deletes these passthroughs.
            // Until then platform-mode forms that depend on these will break:
            // platform-mode shouldn't render tenant-specific forms anyway.

            /**
             * Get organization sectors enum for the active ecosystem.
             * @returns {Promise<Array>} List of valid sectors from the server.
             */
            getSectors: async () => {
                const response = await api.get(
                    '/api/organizations/enums/sectors'
                );
                return response.data;
            },

            /**
             * Get regions enum for the active ecosystem.
             * @returns {Promise<Array>} List of valid regions from the server.
             */
            getRegions: async () => {
                const response = await api.get(
                    '/api/organizations/enums/regions'
                );
                return response.data;
            },

            /**
             * Get the programming types enum for the active ecosystem.
             * @returns {Promise<Array>} List of valid programming types.
             */
            getProgrammingTypes: async () => {
                const response = await api.get(
                    '/api/organizations/enums/programming-types'
                );
                return response.data;
            },

            /**
             * Upload organization logo (authenticated)
             * @param {File} file - Image file to upload
             * @returns {Promise<Object>} Object with imageUrl property
             */
            uploadLogo: async (file) => {
                const formData = new FormData();
                formData.append('logo', file);
                const response = await api.post(
                    '/api/organizations/upload-logo',
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                );
                return response.data;
            },

            /**
             * Claim ownership of an organization (authenticated)
             * @param {string} id - Organization ID
             * @param {Object} claimData - Claim details (roleAtOrg, additionalInfo)
             * @returns {Promise<Object>} Created claim
             */
            claimOwnership: async (id, claimData) => {
                const response = await api.post(
                    `/api/organizations/${id}/claim`,
                    claimData
                );
                return response.data;
            },

            /**
             * Cancel an ownership claim (authenticated)
             * @param {string} id - Organization ID
             * @returns {Promise<Object>} Cancellation confirmation
             */
            cancelClaim: async (id) => {
                const response = await api.delete(
                    `/api/organizations/${id}/claim`
                );
                return response.data;
            },

            /**
             * Fetch an organization's managers (authenticated, manager only)
             * @param {string} id - Organization ID
             * @returns {Promise<Array>} List of managers
             */
            fetchManagers: async (id) => {
                const response = await api.get(
                    `/api/organizations/${id}/managers`
                );
                return response.data;
            },

            /**
             * Grant management on an organization (authenticated, admin or owner)
             * @param {string} id - Organization ID
             * @param {Object} managerData - Grant details (userId, permissionRole)
             * @returns {Promise<Object>} Updated organization
             */
            addManager: async (id, managerData) => {
                const response = await api.post(
                    `/api/organizations/${id}/managers`,
                    managerData
                );
                return response.data;
            },

            /**
             * Revoke management on an organization (authenticated, admin or owner)
             * @param {string} id - Organization ID
             * @param {string} userId - User ID
             * @returns {Promise<Object>} Updated organization
             */
            removeManager: async (id, userId) => {
                const response = await api.delete(
                    `/api/organizations/${id}/managers/${userId}`
                );
                return response.data;
            },

            /**
             * Transfer ownership of an organization (authenticated, owner only)
             * @param {string} id - Organization ID
             * @param {string} newOwnerId - New owner's user ID
             * @returns {Promise<Object>} Updated organization
             */
            transferOwnership: async (id, newOwnerId) => {
                const response = await api.post(
                    `/api/organizations/${id}/transfer-ownership`,
                    { newOwnerId }
                );
                return response.data;
            },

            // ===== Relationship Management =====

            /**
             * Get relationships for an organization
             * @param {string} id - Organization ID
             * @returns {Promise<Object>} Relationships data
             */
            getRelationships: async (id) => {
                const response = await api.get(
                    `/api/organizations/${id}/relationships`
                );
                return response.data;
            },

            /**
             * Create a relationship between organizations
             * @param {string} id - Source organization ID
             * @param {string} targetId - Target organization ID
             * @param {string} type - Relationship type (PARTNERS_WITH, COLLABORATES_WITH)
             * @returns {Promise<Object>} Created relationship
             */
            createRelationship: async (id, targetId, type) => {
                const response = await api.post(
                    `/api/organizations/${id}/relationships`,
                    { targetId, type }
                );
                return response.data;
            },

            /**
             * Delete a relationship between organizations
             * @param {string} id - Source organization ID
             * @param {string} targetId - Target organization ID
             * @param {string} type - Relationship type (optional)
             * @returns {Promise<void>}
             */
            deleteRelationship: async (id, targetId, type = '') => {
                await api.delete(
                    `/api/organizations/${id}/relationships/${targetId}`,
                    { params: type ? { type } : {} }
                );
            },

            /**
             * Fetch sector and region distribution stats (public)
             * @returns {Promise<Object>} Distribution stats with bySector, byRegion arrays
             */
            fetchDistributionStats: async () => {
                const response = await api.get('/api/organizations/stats');
                return response.data;
            },

            /**
             * Fetch users affiliated with an organization (public affiliations only)
             * @param {string} id - Organization ID
             * @param {number} limit - Maximum number of users to return
             * @returns {Promise<Object>} Affiliated users list
             */
            fetchAffiliatedUsers: async (id, limit = 20) => {
                const response = await api.get(
                    `/api/organizations/${id}/affiliated`,
                    { params: { limit } }
                );
                return response.data;
            },
        }),
        [api]
    );
};

export default useOrganizationApi;
